import {
	WORKFRONT_KEPT_FIELDS,
	type WorkfrontKeptField,
	WorkfrontTargetLevel,
	workfrontLevelOf,
	workfrontOwnedFieldsOf,
} from '@atlas/types'
import { isFromWorkfront } from '@/components/work-orders/WorkfrontBadges'
import type { WorkOrderFormState } from './form-model.state'

// Which of the work order form's fields a person may change: on an order from Workfront, the
// ones the sync writes are read-only. The server refuses the same fields, from the same list.

/** Form fields a person cannot change on this order. */
export type LockedFields = ReadonlySet<keyof WorkOrderFormState>

export const NO_LOCKED_FIELDS: LockedFields = new Set()

/** The note under a field the Workfront sync owns. */
export const LOCKED_NOTE = 'Changed in Workfront'

/**
 * Fields that describe one asset and do not apply to a brief: its type and complexity, its costs
 * (a brief's roll up from its assets) and its parent (a brief is always top level). On a
 * Workfront brief they are read-only, so the form does not ask for them.
 */
const ASSET_ONLY_FIELDS: (keyof WorkOrderFormState)[] = [
	'assetTypeCode',
	'complexityLevel',
	'plannedCost',
	'finalCost',
	'parentWorkOrderId',
]

/** Fields locked on a Workfront parent order (brief). */
export const WORKFRONT_PARENT_FIELDS: LockedFields = new Set<keyof WorkOrderFormState>([
	...workfrontOwnedFieldsOf(WorkfrontTargetLevel.PARENT),
	...ASSET_ONLY_FIELDS,
])

/** Fields locked on a Workfront child order (asset) that has its size and asset type. */
export const WORKFRONT_CHILD_FIELDS: LockedFields = new Set<keyof WorkOrderFormState>(
	workfrontOwnedFieldsOf(WorkfrontTargetLevel.CHILD),
)

/** The parts of a work order the lock depends on. */
interface LockedOrder extends Record<WorkfrontKeptField, string | null> {
	source: string
	parentWorkOrderId?: string | null
}

/**
 * What the form locks on `order`: the Workfront targets of its own level. An asset's size or
 * asset type stays open while it has none, because the sync keeps a value set in ATLAS when the
 * Workfront record has none, and the asset cannot be priced without both.
 */
export function lockedFieldsOf(order: LockedOrder | undefined): LockedFields {
	if (!order || !isFromWorkfront(order)) return NO_LOCKED_FIELDS
	if (workfrontLevelOf(order) === WorkfrontTargetLevel.PARENT) return WORKFRONT_PARENT_FIELDS
	const unfilled = new Set<keyof WorkOrderFormState>(WORKFRONT_KEPT_FIELDS.filter((field) => !order[field]))
	if (unfilled.size === 0) return WORKFRONT_CHILD_FIELDS
	return new Set([...WORKFRONT_CHILD_FIELDS].filter((field) => !unfilled.has(field)))
}
